import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CaretLeft, CaretRight, MapPin, Phone, Calendar as CalIcon, CloudRain, Eye } from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";
import { computeEventState, STATE_STYLES } from "@/mobile/eventStatus";
import ExpedienteQuickView from "@/components/expediente/ExpedienteQuickView";

const fmtDayLabel = (d) =>
  d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
const ymd = (d) => d.toISOString().slice(0, 10);
const hhmm = (iso) => new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

export default function MobileAgenda() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialDate = params.get("date") ? new Date(params.get("date")) : new Date();
  const [date, setDate] = useState(initialDate);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Reloj que refresca el estado «retrasado» cada 30s sin tocar el server
  const [now, setNow] = useState(new Date());
  const [quickViewExpId, setQuickViewExpId] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    const dKey = ymd(date);
    api
      .get("/calendar/events", { params: { date_from: dKey, date_to: dKey } })
      .then((r) => {
        // Filtramos por perito actual aunque el backend ya lo hace para no admins
        const mine = (r.data || []).filter((e) => e.perito_id === user?.id || user?.role === "admin");
        // Solo VISITAS y videoperitajes — los juicios/otros se ignoran en la vista del día del perito
        const visits = mine.filter((e) => !e.tipo || e.tipo === "visita" || e.tipo === "videoperitaje");
        visits.sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));
        setEvents(visits);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [date, user]);

  const shift = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d);
    setParams({ date: ymd(d) });
  };
  const goToday = () => {
    const d = new Date();
    setDate(d);
    setParams({});
  };

  const counts = useMemo(() => {
    const c = { pending: 0, visited: 0, delayed: 0 };
    for (const ev of events) {
      const s = computeEventState(ev, now);
      if (s === "pending") c.pending++;
      else if (s === "visited") c.visited++;
      else if (s === "delayed") c.delayed++;
    }
    return c;
  }, [events, now]);

  const isToday = ymd(date) === ymd(new Date());

  return (
    <MobileShell title="Mi agenda" right={
      <button onClick={() => navigate("/m/meteo")}
              data-testid="mobile-agenda-meteo-link"
              className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-semibold px-2 py-1 rounded hover:bg-blue-50">
        <CloudRain className="w-3.5 h-3.5" weight="fill" /> Meteo
      </button>
    }>
      {/* Selector de día */}
      <div className="bg-white border-b border-[#E8EAED] px-3 py-3 sticky top-12 z-10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => shift(-1)}
            className="p-2 rounded-md hover:bg-zinc-100"
            data-testid="agenda-prev-day"
            aria-label="Día anterior"
          >
            <CaretLeft className="w-4 h-4" weight="bold" />
          </button>
          <div className="flex-1 text-center">
            <div className="text-[15px] font-semibold capitalize" data-testid="agenda-date-label">{fmtDayLabel(date)}</div>
            {!isToday && (
              <button
                onClick={goToday}
                className="text-[11px] text-blue-600 hover:underline mt-0.5"
                data-testid="agenda-go-today"
              >
                Ir a hoy
              </button>
            )}
          </div>
          <button
            onClick={() => shift(1)}
            className="p-2 rounded-md hover:bg-zinc-100"
            data-testid="agenda-next-day"
            aria-label="Día siguiente"
          >
            <CaretRight className="w-4 h-4" weight="bold" />
          </button>
        </div>
        {/* Contadores */}
        <div className="mt-2 flex items-center justify-center gap-2 text-[11px]">
          <Counter color="blue" label="Pendientes" n={counts.pending} testid="count-pending" />
          <Counter color="emerald" label="Visitados" n={counts.visited} testid="count-visited" />
          <Counter color="red" label="Retrasados" n={counts.delayed} testid="count-delayed" />
        </div>
      </div>

      <div className="px-3 pt-3">
        {loading && (
          <div className="text-center text-muted text-xs py-8">Cargando agenda…</div>
        )}
        {!loading && events.length === 0 && (
          <EmptyState date={date} isToday={isToday} />
        )}
        <div className="space-y-2">
          {events.map((ev) => (
            <EventCard
              key={ev.id}
              ev={ev}
              now={now}
              onClick={() => navigate(`/m/agenda/event/${encodeURIComponent(ev.id)}`)}
              onQuickView={() => setQuickViewExpId(ev.expediente_id)}
            />
          ))}
        </div>
      </div>
      <ExpedienteQuickView
        expedienteId={quickViewExpId}
        onClose={() => setQuickViewExpId(null)}
      />
    </MobileShell>
  );
}

function Counter({ color, label, n, testid }) {
  const tone = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[color];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tone}`} data-testid={testid}>
      <strong className="font-semibold">{n}</strong>
      <span>{label}</span>
    </span>
  );
}

function EventCard({ ev, now, onClick, onQuickView }) {
  const state = computeEventState(ev, now);
  const sty = STATE_STYLES[state];
  const tel = ev.asegurado_movil || ev.asegurado_telefono;
  const mapsQ = ev.location ? encodeURIComponent(ev.location) : null;

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      role="button"
      tabIndex={0}
      className={`w-full text-left rounded-lg shadow-sm border border-[#E8EAED] p-3 ${sty.card} hover:shadow transition-shadow active:translate-y-px cursor-pointer`}
      data-testid={`event-card-${ev.id}`}
      data-state={state}
    >
      <div className="flex items-start gap-2">
        <div className="flex flex-col items-center pt-0.5 w-12 shrink-0">
          <div className="text-[18px] font-bold tabular-nums">{hhmm(ev.starts_at)}</div>
          <span className={`mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide border ${sty.badge}`}>
            {sty.label}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[14px] truncate">{ev.asegurado_nombre || ev.title || "—"}</div>
          {ev.expediente_numero && (
            <div className="text-[11px] text-muted truncate">Nº {ev.expediente_numero}</div>
          )}
          {ev.location && (
            <div className="text-[12px] mt-1 text-zinc-700 line-clamp-2">{ev.location}</div>
          )}
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="mt-2 flex gap-1.5">
        {ev.expediente_id && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onQuickView?.(); }}
            className="flex-1 inline-flex items-center justify-center gap-1 bg-orange-50 border border-orange-200 text-orange-800 text-[12px] font-medium py-2 rounded-md hover:bg-orange-100"
            data-testid={`event-quick-view-${ev.id}`}
          >
            <Eye className="w-3.5 h-3.5" weight="fill" /> Ver
          </button>
        )}
        {mapsQ && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${mapsQ}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 inline-flex items-center justify-center gap-1 bg-white border border-[#E8EAED] text-[12px] font-medium py-2 rounded-md hover:bg-zinc-50"
            data-testid={`event-quick-map-${ev.id}`}
          >
            <MapPin className="w-3.5 h-3.5" weight="bold" /> Mapa
          </a>
        )}
        {tel && (
          <a
            href={`tel:${tel}`}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 inline-flex items-center justify-center gap-1 bg-white border border-[#E8EAED] text-[12px] font-medium py-2 rounded-md hover:bg-zinc-50"
            data-testid={`event-quick-tel-${ev.id}`}
          >
            <Phone className="w-3.5 h-3.5" weight="bold" /> Llamar
          </a>
        )}
      </div>
    </div>
  );
}

function EmptyState({ date, isToday }) {
  return (
    <div className="bg-white rounded-lg border border-[#E8EAED] p-8 text-center" data-testid="agenda-empty">
      <CalIcon className="w-8 h-8 mx-auto text-muted" weight="duotone" />
      <div className="mt-2 text-[14px] font-semibold">
        {isToday ? "Sin visitas para hoy" : `Sin visitas el ${date.toLocaleDateString("es-ES")}`}
      </div>
      <div className="text-[12px] text-muted mt-1">Disfruta del descanso o programa una visita.</div>
    </div>
  );
}
